import { NextRequest } from 'next/server';

import { env } from '@/constant/env';
import { Logger } from '@/lib/Logging';

import { POSTHOG_CONFIG, TIMEOUT_DURATIONS } from './constants';
import { PosthogCookie, PosthogDecideResponse } from './types';
const logger = new Logger('experiments/route.ts');

const getPhApiKey = () => env.publicPosthogApiKey;

export const getPosthogDistinctId = (request: NextRequest): string => {
  const phApiKey = getPhApiKey();

  if (!phApiKey) {
    return crypto.randomUUID();
  }

  const ph_cookie_key = `ph_${phApiKey}_posthog`;
  const cookie = request.cookies.get(ph_cookie_key);

  if (!cookie?.value) {
    return crypto.randomUUID();
  }

  try {
    const parsed = JSON.parse(cookie.value) as PosthogCookie;
    return parsed.distinct_id || crypto.randomUUID();
  } catch {
    return crypto.randomUUID();
  }
};

export const getExperimentsFromPosthog = async (
  distinct_id: string
): Promise<Record<string, string>> => {
  const phApiKey = getPhApiKey();

  if (!phApiKey) {
    return {};
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      TIMEOUT_DURATIONS.DEFAULT_REQUEST
    );

    const response = await fetch(
      `${POSTHOG_CONFIG.HOST}${POSTHOG_CONFIG.DECIDE_ENDPOINT}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: phApiKey,
          distinct_id: distinct_id,
        }),
        signal: controller.signal,
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      return {};
    }

    const data = (await response.json()) as PosthogDecideResponse;
    const featureFlags = data?.featureFlags || {};

    const experiments = Object.keys(featureFlags).reduce((acc, key) => {
      if (key.startsWith(POSTHOG_CONFIG.EXPERIMENT_PREFIX)) {
        acc[key] = featureFlags[key];
      }
      return acc;
    }, {} as Record<string, string>);

    return experiments;
  } catch (error) {
    logger.error({
      message: 'Error getting experiments from posthog',
      context: {
        error,
      },
    });
    return {};
  }
};
